import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * /uitschrijven — one-click unsubscribe destination for newsletter footers.
 *
 * The link carries `email` + `token` (HMAC computed server-side). We first
 * verify() so we can differentiate three states without submitting a
 * bogus POST:
 *   – invalid token    → "Deze link is ongeldig"
 *   – already off list → "Je bent al uitgeschreven"
 *   – valid & new      → show single confirm button
 *
 * The confirm POSTs back to /api/uitschrijven. RFC 8058 clients (Gmail,
 * Yahoo) hit that endpoint directly via List-Unsubscribe-Post — this
 * page is only rendered when a human actually clicks the footer link.
 */
const Uitschrijven = () => {
  const [params] = useSearchParams();
  const email = params.get('email') || '';
  const token = params.get('token') || '';

  const [state, setState] = useState('loading'); // loading | ready | already | invalid | done | error
  const [error, setError] = useState('');

  useEffect(() => {
    if (!email || !token) { setState('invalid'); return; }
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/uitschrijven/verify?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`,
        );
        const d = await res.json();
        if (!d.ok) { setState('invalid'); return; }
        setState(d.already_unsubscribed ? 'already' : 'ready');
      } catch (e) {
        setState('invalid');
      }
    })();
  }, [email, token]);

  const confirm = async () => {
    setState('loading');
    try {
      const res = await fetch(`${API_URL}/api/uitschrijven`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Er ging iets mis.');
      setState('done');
    } catch (e) {
      setError(e.message);
      setState('error');
    }
  };

  return (
    <div style={styles.wrap} data-testid="uitschrijven-page">
      <Helmet>
        <title>Uitschrijven — CasaOthers</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div style={styles.card}>
        {state === 'loading' && (
          <>
            <Loader2 size={40} className="spin" color="#C5A059" />
            <h1 style={styles.h1}>Even geduld…</h1>
            <p style={styles.p}>We controleren je uitschrijf-link.</p>
          </>
        )}

        {state === 'ready' && (
          <>
            <h1 style={styles.h1}>Uitschrijven van de nieuwsbrief</h1>
            <p style={styles.p}>
              Je staat op het punt <strong>{email}</strong> uit te schrijven
              van de CasaOthers nieuwsbrief. Je ontvangt daarna geen mails meer
              van ons, tenzij je opnieuw expliciet toestemming geeft.
            </p>
            <button
              style={styles.btn}
              onClick={confirm}
              data-testid="uitschrijven-confirm"
            >
              Ja, schrijf mij uit
            </button>
            <Link to="/" style={styles.linkMuted} data-testid="uitschrijven-cancel">
              Toch niet — terug naar de site
            </Link>
          </>
        )}

        {state === 'already' && (
          <>
            <CheckCircle2 size={40} color="#166534" />
            <h1 style={styles.h1}>Je bent al uitgeschreven</h1>
            <p style={styles.p}>
              Het adres <strong>{email}</strong> staat niet meer op onze
              verzendlijst. Je hoeft verder niets te doen.
            </p>
            <Link to="/" style={styles.btnLink} data-testid="uitschrijven-home">
              Terug naar CasaOthers
            </Link>
          </>
        )}

        {state === 'done' && (
          <>
            <CheckCircle2 size={40} color="#166534" />
            <h1 style={styles.h1}>Uitgeschreven ✓</h1>
            <p style={styles.p}>
              Je verzoek is verwerkt. We versturen geen nieuwsbrieven meer naar
              <strong> {email}</strong>. Bedankt dat je even hebt aangegeven
              waar je staat — dat helpt ons enorm.
            </p>
            <Link to="/" style={styles.btnLink} data-testid="uitschrijven-home">
              Terug naar CasaOthers
            </Link>
          </>
        )}

        {state === 'invalid' && (
          <>
            <XCircle size={40} color="#991B1B" />
            <h1 style={styles.h1}>Deze link werkt niet</h1>
            <p style={styles.p}>
              De uitschrijf-link is verlopen of ongeldig. Open de meest recente
              nieuwsbrief die je van ons hebt gekregen en klik daar op de
              uitschrijf-link, of mail ons direct via&nbsp;
              <a href="mailto:info@casaothers.nl" style={styles.linkGold}>info@casaothers.nl</a>.
            </p>
            <Link to="/" style={styles.btnLink}>Terug naar CasaOthers</Link>
          </>
        )}

        {state === 'error' && (
          <>
            <XCircle size={40} color="#991B1B" />
            <h1 style={styles.h1}>Er ging iets mis</h1>
            <p style={styles.p}>{error}</p>
            <button style={styles.btn} onClick={confirm}>Probeer opnieuw</button>
          </>
        )}
      </div>

      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

const styles = {
  wrap: { minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem 1.5rem', background: '#FAF9F6' },
  card: { maxWidth: '480px', width: '100%', background: 'white', border: '1px solid #EAE5DD', padding: '2.5rem 2rem', textAlign: 'center' },
  h1: { fontFamily: "'Playfair Display', serif", fontSize: '1.75rem', color: '#1A1918', margin: '1rem 0 0.75rem' },
  p: { color: '#585755', fontSize: '0.95rem', lineHeight: 1.6, margin: '0 0 1.5rem' },
  btn: {
    display: 'inline-block', padding: '0.85rem 1.5rem', background: '#1A1918', color: '#C5A059',
    border: 'none', fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.08em', textTransform: 'uppercase',
    cursor: 'pointer', marginBottom: '1rem',
  },
  btnLink: {
    display: 'inline-block', padding: '0.85rem 1.5rem', background: '#1A1918', color: '#C5A059',
    fontWeight: 700, fontSize: '0.85rem', letterSpacing: '0.08em', textTransform: 'uppercase',
    textDecoration: 'none',
  },
  linkMuted: { display: 'block', marginTop: '0.5rem', color: '#8C8B88', fontSize: '0.85rem', textDecoration: 'underline' },
  linkGold: { color: '#C5A059', textDecoration: 'underline' },
};

export default Uitschrijven;
